import Button from '@ui/Button';
import { useUpgrade } from '@util/hooks/useUpgrade';
import { useState } from 'react';
import BaseModal from './BaseModal';
import { OrderDocument } from 'models/order';
import { useToastContext } from 'context/ToastContext';
import { getUserById } from '@util/firestore/users';

export const UpgradeOrderModal = ({
  open,
  onClose,
  order,
  seller_id,
  item_id,
  chat_id,
  initialReason,
}: {
  open: boolean;
  onClose: () => void;
  order: OrderDocument;
  seller_id: string;
  item_id: string;
  chat_id?: string;
  initialReason?: string;
}) => {
  const [chargeSeller, setChargeSeller] = useState(true);
  const [reason, setReason] = useState(
    initialReason ?? 'did_not_ship_in_required_time_period'
  );
  const { showErrorToast } = useToastContext();
  const { upgradeMutation } = useUpgrade({
    onSuccess: () => {
      onClose();
    },
  });
  const getFee = () => {
    const items = order.sellers[seller_id].filter(
      (item) => item.id === item_id
    );
    const subTotal =
      items.reduce((acc, item) => acc + item.product_cost * item.qty, 0) -
      (order.coupon?.seller_id === seller_id ? order.coupon?.off ?? 0 : 0);
    const percentage = 0.1;
    const min = 10;

    const cancellationCost = Math.max(subTotal * percentage, min);
    return cancellationCost;
  };
  return (
    <BaseModal isOpen={open} dismiss={onClose}>
      <div className="flex min-w-[320px] flex-col gap-6 p-6">
        <h2 className="text-2xl font-bold">Upgrade Order</h2>
        <div className="flex items-center gap-4">
          <input
            id="chargeSellerCheckbox"
            type="checkbox"
            checked={chargeSeller}
            onChange={(e) => setChargeSeller(e.target.checked)}
          />
          <label htmlFor="chargeSellerCheckbox" className="text-[base]">
            Charge seller cancellation fee {getFee() ? `(${getFee()} USD)` : ''}
          </label>
        </div>
        <div className="flex flex-col gap-2">
          <label
            htmlFor="upgradeReasonSelect"
            className="text-[2rem] font-medium"
          >
            Reason
          </label>
          <select
            id="upgradeReasonSelect"
            className="rounded border p-4"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          >
            <option value="did_not_ship_in_required_time_period">
              Did not ship in required time period
            </option>
            <option value="item_no_longer_in_stock">
              Item no longer in stock
            </option>
            <option value="shipping_complications">
              Shipping complications
            </option>
            <option value="out_of_town">Out of town</option>
            <option value="other">Other</option>
          </select>
        </div>
        <div className="mt-8 flex justify-end gap-8">
          <Button text="Cancel" type="secondary" onClick={onClose} />
          <Button
            text="Confirm Upgrade"
            type="success"
            loading={upgradeMutation.isPending}
            disabled={upgradeMutation.isPending}
            onClick={async () => {
              const item = order.sellers[seller_id]?.find(
                (i) => i.id === item_id
              );
              if (!item) {
                showErrorToast('Item not found');
                return;
              }
              const user = await getUserById(seller_id);
              if (!user) {
                showErrorToast('Seller not found');
                return;
              }
              upgradeMutation.mutate({
                order,
                item,
                sellerUsername: user.username,
                seller_id: seller_id,
                order_id: order.id,
                reason,
                chargeSeller,
                chat_id,
                fee: getFee(),
              });
            }}
          />
        </div>
      </div>
    </BaseModal>
  );
};
